import { useCallback, useState } from 'react';
import { parseDateFields } from '../http/useHttpFetch';
import { StudyParticipantEventFormDto } from './useGetStudyParticipantEventForm';
import { guardedPut } from '../../api/guardedPut';
import { FetchError } from '../../api/api';

export interface StudyParticipantFormEventUpsertDto {
  studyParticipantId: number;
  participantEventId: number;
  participantEventLogId?: number;
  intendedStartDate: Date;
  occurrenceCount: number;
  isFinal?: boolean;
  progress: number;
  formTemplateId: number;
  version: number;
  statePath: string;
  data: any;
  /**
   * duration in minutes spent on form state.
   */
  duration: number;
}

export function useUpsertStudyParticipantEventForm(options?: {
  path?: string;
  accessToken?: string;
}) {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<FetchError>();
  const [upsertedEventData, setUpsertedEventData] =
    useState<StudyParticipantEventFormDto>();

  const upsertEventData = useCallback(
    async (formEntityUpsertDto: StudyParticipantFormEventUpsertDto) => {
      const { studyParticipantId, participantEventId } = formEntityUpsertDto;
      setLoading(true);
      // guardedPut owns retries and never rejects — a timeout, network drop,
      // or non-JSON body surfaces as an error state instead of escaping the
      // hook and leaving the submit spinner stuck (CHLOE-MOBILE-APP-11)
      try {
        const { json, error } = await guardedPut(
          options?.path ||
            `study-participants/${studyParticipantId}/events/${participantEventId}`,
          formEntityUpsertDto,
          options?.accessToken
            ? { accessToken: options.accessToken }
            : undefined
        );
        if (error) {
          setUpsertedEventData(undefined);
          setError(error);
        } else {
          setError(undefined); // a successful resubmit must clear a prior failure
          setUpsertedEventData(json ? parseDateFields(json) : json);
        }
      } finally {
        // even an unexpected parse throw must never leave the spinner stuck
        setLoading(false);
      }
    },
    [options?.accessToken, options?.path]
  );

  return {
    upsertedEventData,
    isUpsertingEventData: loading,
    upsertingEventDataError: error,
    upsertEventData
  };
}
